/**
 * StoneFistSkillEffect — Earth tree stone_fist path (Fork 1) active.
 *
 * Phased skill: wind-up (600ms, uncancellable) → active (hit resolves) →
 * recovery → cooldown (medium, 800ms).
 *
 * On hit:
 * - Single melee hit on the closest enemy in facing direction (pre-Fault Strike)
 * - Or 90-degree cone of up to 3 targets when Fault Strike is unlocked
 * - Ignores knockback resistance: `ignoreKnockbackResistance: true`
 * - Guaranteed stagger regardless of enemy type
 * - If Iron Seam is unlocked AND player is stationary at cast time:
 *   bonus damage multiplied by `(1 + stationaryDamageBonus)`
 *
 * Iron Seam stationary check: captured at wind-up BEGIN (onActivate), not at
 * hit resolution — the player could theoretically move during wind-up but
 * the bonus is committed at swing start.
 *
 * Replaced by HammerSkillEffect (stone_fist_evolved) when that evolution unlocks.
 *
 * Extends PhasedSkillEffect with wind-up non-interruptible override.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Path — Stone Fist
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StoneFistDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(effectId: string, ...args: unknown[]): void;
  };
  /**
   * Targeting layer — returns enemy IDs the game layer resolves at runtime.
   */
  readonly targeting: {
    /** Returns the closest enemy ID in the player's facing direction, or null if none in range. */
    getSingleTarget(): string | null;
    /** Returns up to maxTargets enemy IDs within arcDegrees cone in player's facing direction. */
    getConeTargets(arcDegrees: number, maxTargets: number): string[];
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string; ignoreKnockbackResistance: boolean },
    ): void;
  };
  readonly stationary: {
    isStationary(thresholdMs: number): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StoneFistConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  /** Base damage resolved through CombatHitResolver. */
  readonly baseDamage: number;
  /** Stationary threshold for Iron Seam bonus check (ms). */
  readonly stationaryRequirementMs: number;
}

const DEFAULT_CONFIG: StoneFistConfig = {
  windupMs: 600,           // wind-up: 0.6s
  recoveryMs: 150,         // brief settle after impact
  cooldownMs: 800,         // medium cooldown tier
  baseDamage: 18,          // base melee damage (combat config reference)
  stationaryRequirementMs: 1000,
};

// ---------------------------------------------------------------------------
// Phase sequence
// ---------------------------------------------------------------------------

const PHASE_SEQUENCE: readonly SkillPhase[] = ['windup', 'active', 'recovery', 'cooldown'];

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StoneFistSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'stone_fist';

  protected readonly phaseSequence = PHASE_SEQUENCE;

  private readonly deps: StoneFistDeps;
  private readonly config: StoneFistConfig;

  // Per-activation state
  private _stationaryBonusActive = false;
  private _targetsHit: string[] = [];
  private _cleaveActive = false;
  private _lastDamageDealt = 0;
  private _staggersApplied = 0;
  private _activationCount = 0;

  constructor(deps: StoneFistDeps, config: Partial<StoneFistConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get stationaryBonusActive(): boolean { return this._stationaryBonusActive; }
  get targetsHit(): readonly string[] { return this._targetsHit; }
  get cleaveActive(): boolean { return this._cleaveActive; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get staggersApplied(): number { return this._staggersApplied; }
  get activationCount(): number { return this._activationCount; }

  // --- PhasedSkillEffect overrides ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 16; // single frame — hit resolves immediately on phase enter
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  /** Wind-up is NOT interruptible — committing to the swing is the mechanical cost. */
  override get interruptible(): boolean {
    return this.phase !== 'windup' && this.phase !== 'active';
  }

  protected override onActivate(): void {
    // Reset per-activation state
    this._targetsHit = [];
    this._lastDamageDealt = 0;
    this._staggersApplied = 0;
    this._cleaveActive = false;
    this._activationCount++;

    // Check Iron Seam stationary bonus AT WIND-UP START
    this._stationaryBonusActive =
      this.deps.resolver.hasEffect('stone_fist', 'stationaryDamageBonus') &&
      this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase !== 'active') return;

    // Determine targeting mode at hit resolution
    const hasCleave = this.deps.resolver.hasEffect('stone_fist', 'cleaveArcDegrees');
    this._cleaveActive = hasCleave;

    let targets: string[];
    if (hasCleave) {
      const arcDegrees =
        this.deps.resolver.getAdditiveEffect('stone_fist', 'cleaveArcDegrees');
      const maxTargetsResolved =
        this.deps.resolver.getMaxEffect('stone_fist', 'maxTargets') ?? 3;
      targets = this.deps.targeting.getConeTargets(arcDegrees, maxTargetsResolved);
    } else {
      const singleTarget = this.deps.targeting.getSingleTarget();
      targets = singleTarget !== null ? [singleTarget] : [];
    }

    // Stationary damage bonus (Iron Seam)
    const bonusMultiplier = this._stationaryBonusActive
      ? this.deps.resolver.getAdditiveEffect('stone_fist', 'stationaryDamageBonus')
      : 0;
    const effectiveDamage = this.config.baseDamage * (1 + bonusMultiplier);

    for (const targetId of targets) {
      // Apply damage through CombatHitResolver — knockback resistance bypassed
      this.deps.combat.applyDamage(targetId, effectiveDamage, {
        sourceSkillId: 'stone_fist',
        ignoreKnockbackResistance: true,
      });

      // Guaranteed stagger regardless of enemy type or stagger resistance
      this.deps.statusEffects.apply('stagger', {
        sourceSkillId: 'stone_fist',
        targetId,
        // Stagger duration from StatusEffectDef defaults — no per-skill override
      });
      this._staggersApplied++;
    }

    this._targetsHit = [...targets];
    this._lastDamageDealt = effectiveDamage * targets.length;
    this.markHitApplied();
  }
}
